/**
 * In-cart cross-sell behaviour.
 *
 * Preview: the snippet renders hidden while its mode is "preview". Adding ?upsell=1 to any URL
 * reveals it for this browser only (remembered), ?upsell=0 hides it again. Customers see nothing
 * until the cart drawer's "Cross-sell" setting is switched to "on".
 *
 * Adding uses the theme's own cart handshake (prepare-bundled-sections -> /cart/add.js with
 * sections -> variant:add + cart:change), which is what refreshes and opens the drawer. A bare
 * cart:refresh does not.
 */
(() => {
  const KEY = 'smile:upsell-preview';

  const previewOn = () => {
    try {
      const flag = new URLSearchParams(location.search).get('upsell');
      if (flag === '1') localStorage.setItem(KEY, '1');
      if (flag === '0') localStorage.removeItem(KEY);
      return localStorage.getItem(KEY) === '1';
    } catch (e) {
      return new URLSearchParams(location.search).get('upsell') === '1';
    }
  };

  // While previewing, hide the In Cart Upsell app's own block so this one can be judged on its
  // own. Customers (no preview flag) keep seeing the app until we switch this cross-sell on.
  const APP_OFFER = '#nudge-offer-drawer-icu';

  const reveal = () => {
    if (!previewOn()) return;
    document.querySelectorAll('[data-sp-upsell-preview][hidden]').forEach((el) => el.removeAttribute('hidden'));
    document.querySelectorAll(APP_OFFER).forEach((el) => { el.style.display = 'none'; });
  };

  const clamp = (value) => Math.min(10, Math.max(1, parseInt(value, 10) || 1));

  const step = (button) => {
    const box = button.closest('[data-sp-upsell]');
    const input = box && box.querySelector('[data-sp-upsell-qty-input]');
    if (!input) return;
    input.value = clamp(Number(input.value) + Number(button.dataset.spUpsellStep));
    box.querySelectorAll('[data-sp-upsell-step="-1"]').forEach((b) => { b.disabled = Number(input.value) <= 1; });
  };

  const add = async (button) => {
    const variantId = button.dataset.variantId;
    const box = button.closest('[data-sp-upsell]');
    const error = box && box.querySelector('[data-sp-upsell-error]');
    const qtyInput = box && box.querySelector('[data-sp-upsell-qty-input]');
    const quantity = qtyInput ? clamp(qtyInput.value) : 1;
    if (!variantId || button.disabled) return;
    button.disabled = true;
    button.classList.add('is-busy');
    if (error) error.hidden = true;
    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: sections.join(','), sections_url: `/variants/${variantId}` })
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || 'Could not add to cart.');
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cart.sections = json.sections;
      document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart, blockCartDrawerOpening: true } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } catch (e) {
      if (error) {
        error.textContent = e.message;
        error.hidden = false;
      }
    } finally {
      button.disabled = false;
      button.classList.remove('is-busy');
    }
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest) return;
    const stepper = event.target.closest('[data-sp-upsell-step]');
    if (stepper) {
      event.preventDefault();
      step(stepper);
      return;
    }
    const button = event.target.closest('[data-sp-upsell-add]');
    if (button) {
      event.preventDefault();
      add(button);
    }
  });

  // The drawer rewrites its own markup after the cart events fire, so revealing on those events
  // alone loses the race: watch the drawer instead and re-reveal whenever it re-renders.
  const watch = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__smileUpsellWatched) return;
    drawer.__smileUpsellWatched = true;
    new MutationObserver(reveal).observe(drawer, { childList: true, subtree: true });
  };

  const start = () => {
    watch();
    reveal();
  };

  document.addEventListener('cart:change', start);
  document.addEventListener('cart:refresh', start);
  document.addEventListener('DOMContentLoaded', start);
  start();
})();
